import { Eyebrow, LinkButton } from '@/components/ui';
import { ArrowRightIcon, BarbellIcon, GridIcon, MonitorIcon, SparkIcon } from '@/lib/icons';

/**
 * The CRM hero. This is the coaches-and-gyms audience only — per platform plan
 * §3 the lifter proposition never appears here, and the CRM never appears in
 * the lifter hero. One orange primary action, everything else ink.
 */
export function CrmHero() {
  return (
    <section id="crm-hero" className="relative overflow-hidden border-b border-hairline bg-page">
      <div aria-hidden="true" className="l2f-halftone pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto grid max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] items-start gap-10 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:pt-16">
        <div className="flex flex-col items-start gap-6">
          <Eyebrow>
            <GridIcon className="mr-1 inline text-sm" /> For coaches &amp; gyms · Lift2Fit CRM
          </Eyebrow>

          <h1 className="font-display text-[33px] font-black uppercase leading-[1.04] tracking-[-0.03em] text-ink sm:text-[46px]">
            Run your clients,
            <br />
            <span className="text-accent">not your spreadsheets.</span>
          </h1>

          <p className="max-w-[68ch] text-body-lg text-ink-muted">
            Lift2Fit for coaches and gyms is the desk side of the platform: your roster, your clients’ programmes,
            your calendar and bookings, your payments and revenue — with AI assistance reading across every client.
            It is built for coaches, performance gyms, studios and recovery centres who run programmes
            for named clients. It is not a regular gym’s front desk.
          </p>

          <p
            data-testid="crm-slogan"
            className="border-l-2 border-accent pl-4 font-display text-[19px] font-black leading-snug tracking-[-0.01em] text-ink sm:text-[22px]"
          >
            One stop for all your fitness needs.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            {/* The front door: one orange primary action. Talk to us stays for
                the Scale tier (100+ clients) — a deliberate human path. */}
            <LinkButton href="#/crm/signup" size="lg" data-testid="crm-hero-start-coaching">
              Start coaching <ArrowRightIcon className="text-sm" />
            </LinkButton>
            <LinkButton href="mailto:info@lift2fit.com" variant="outline" size="lg" data-testid="crm-hero-talk">
              Talk to us
            </LinkButton>
            <LinkButton href="#crm-pricing" variant="outline" size="lg">
              See the tiers
            </LinkButton>
          </div>

          <ul className="flex flex-wrap gap-2">
            {['Coaches', 'Performance gyms', 'Studios', 'Recovery centres'].map((audience) => (
              <li
                key={audience}
                className="rounded-pill border border-hairline px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink"
              >
                {audience}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-4 rounded-card border border-hairline bg-surface p-6 shadow-modal">
          <span className="inline-flex h-10 w-10 items-center justify-center bg-brand-charcoal text-brand-white">
            <MonitorIcon className="text-lg" />
          </span>
          <h2 className="font-display text-[18px] font-black uppercase tracking-[-0.01em] text-ink">
            Web only. A desk tool.
          </h2>
          <p className="text-[13px] leading-relaxed text-ink-muted">
            There is no phone or watch app for the CRM. A coach who also trains uses the lifter interface for
            their own log — the roster and the log are the same platform seen from two ends.
          </p>
          <p
            data-testid="crm-free-lifter-promo"
            className="border-l-2 border-accent pl-3 text-[13px] font-bold leading-snug text-ink"
          >
            A coaching workspace, and a free lifter account for your own training.
          </p>
          <ul className="flex flex-col gap-3 border-t border-hairline pt-4 text-[13px] text-ink">
            {[
              { icon: <BarbellIcon className="text-accent" />, line: 'Your clients’ own programmes and grids, read and prescribed' },
              { icon: <GridIcon className="text-accent" />, line: 'Availability slots with enforced capacity' },
              { icon: <SparkIcon className="text-accent" />, line: 'AI assistance across the whole roster' },
            ].map((item) => (
              <li key={item.line} className="flex items-start gap-2.5">
                <span className="mt-[2px] shrink-0 text-sm">{item.icon}</span>
                <span className="leading-snug">{item.line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
